import React, { useState } from 'react';

import * as Yup from 'yup';
import { t } from 'i18next';
import { useFormik } from 'formik';

import toast from 'react-hot-toast';
import { Close } from '@mui/icons-material';
import { EditIcon } from '../../../../assets/images/icons'; // Replace with correct import for the icon
import { useDispatch, useSelector } from 'react-redux';

import Button from '../../Buttons/Button/Button';
import styles from './EditResumeAndCoverLetter.module.css';
import Subtitle from '../Subtitle/Subtitle';
import BasicPopup from '../../Popups/BasicPopup/BasicPopup';
import { updateUser } from '../../../../services/userService';
import { fetchUserByToken, fetchUserByTokenWithoutLoading } from '../../../../app/features/userSlice';
import CVPairList from '../CVPair/CVPairListList/CVPairList';
import FormFileField from '../../FormUI/FormFileField/FormFileField';
import FlatButton from '../../../../components/UI/Buttons/FlatButton/FlatButton';
import download_icon from '../../../../assets/images/icons/download.svg';
import CVAndCoverLetter from '../../../Forms/Common/CVAndCoverLetter/CVAndCoverLetter';

function EditResumeAndCoverLetter({ }) {
    const user = useSelector(state => state.user.user);
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true)
    const [isOpenCVSelector, setIsOpenCVSelector] = useState(false);

    const userToken = useSelector(state => state.user.token);
    const dispatch = useDispatch()

    const generateCVPairs = (user) => {
        const cvPairs = [];
        let primarySet = false;

        // First check numbered CV pairs
        for (let i = 0; i < 5; i++) {
            const cvFileKey = `cv_file_${i}`;
            const cvRefLetterKey = `cv_ref_letter_${i}`;

            if (user[cvFileKey] && user[cvRefLetterKey]) {
                const isPrimary = !primarySet && user[cvFileKey] === user['primary_cv_file'];
                const extractExtension = (filePath) => {
                    const fileNameMatch = filePath.match(/(?:[^/\\]+\/)*([^/\\]+\.\w+)$/);
                    if (fileNameMatch) {
                        const fileName = fileNameMatch[1];
                        const ext = fileName.split('.').pop();
                        return ext; // Returns the extension, e.g., "pdf"
                    }
                    return ''; // If no match found, return an empty string
                };
                const extractName = (filePath) => {
                    const extension = extractExtension(filePath);
                    const nameMatch = filePath.match(/name:([^-.]+)/);
                    return nameMatch ? nameMatch[1] + "." + extension : '';
                };

                cvPairs.push({
                    id: Date.now() + i,
                    isPrimary: isPrimary,
                    cv_file: {
                        name: extractName(user[cvFileKey]),
                        path: user[cvFileKey]
                    },
                    cv_ref_letter: {
                        name: extractName(user[cvRefLetterKey]),
                        path: user[cvRefLetterKey],
                    },
                    video_cv: user[`video_cv_${i}`] ? {
                        name: extractName(user[`video_cv_${i}`]),
                        path: user[`video_cv_${i}`]
                    } : null
                });

                if (isPrimary) {
                    primarySet = true;
                }
            }
        }

        // If no numbered pairs found, check for default cv_file and cv_ref_letter
        if (cvPairs.length === 0 && user.cv_file && user.cv_ref_letter) {
            const isPrimary = user.cv_file === user['primary_cv_file'];
            const extractExtension = (filePath) => {
                const fileNameMatch = filePath.match(/(?:[^/\\]+\/)*([^/\\]+\.\w+)$/);
                if (fileNameMatch) {
                    const fileName = fileNameMatch[1];
                    const ext = fileName.split('.').pop();
                    return ext;
                }
                return '';
            };
            const extractName = (filePath) => {
                const extension = extractExtension(filePath);
                const nameMatch = filePath.match(/name:([^-.]+)/);
                return nameMatch ? nameMatch[1] + "." + extension : '';
            };

            cvPairs.push({
                id: Date.now(),
                isPrimary: isPrimary,
                cv_file: {
                    name: extractName(user.cv_file),
                    path: user.cv_file
                },
                cv_ref_letter: {
                    name: extractName(user.cv_ref_letter),
                    path: user.cv_ref_letter,
                },
                video_cv: user.video_cv ? {
                    name: extractName(user.video_cv),
                    path: user.video_cv
                } : null
            });
        }

        // If no match was found, set the first entry's isPrimary to true
        if (!primarySet && cvPairs.length > 0) {
            cvPairs[0].isPrimary = true;
        }

        return cvPairs;
    };

    const formik = useFormik({
        initialValues: {
            cv_Pair: generateCVPairs(user)?.length > 0 ? generateCVPairs(user) : [], // Use the generated CV pairs
            cv_file: '',
            cv_ref_letter: '',
            video_cv: '',
        },
        validationSchema: Yup.object({
        }),
        onSubmit: (values) => {
            if (formLoading) {
                return;
            }
            setFormLoading(true);
            const formData = new FormData();
            formData.append('token', userToken); // Assuming you have user token

            if (values.cv_file && values.cv_file !== '') {
                formData.append('cv_file', values.cv_file);
                // Set this as primary CV if no primary has been set
                if (!values.cv_Pair || values.cv_Pair.length === 0) {
                    formData.append('primary_cv_file', 'auto');
                }
            }
            if (values.cv_ref_letter && values.cv_ref_letter !== '') {
                formData.append('cv_ref_letter', values.cv_ref_letter);
            }
            if (values.video_cv && values.video_cv !== '') {
                formData.append('video_cv', values.video_cv);
            }

            if (values?.cv_Pair && values?.cv_Pair[0]) {
                const pair0 = values.cv_Pair[0];
                const cvFileType0 = pair0?.cv_file instanceof File;
                const coverLetterType0 = pair0?.cv_ref_letter instanceof File;
                const videoCVType0 = pair0?.video_cv instanceof File;

                if (pair0?.cv_file) {
                    if (pair0?.isPrimary == true) {
                        formData?.append('primary_cv_file', pair0?.cv_file.path);
                    }
                    formData?.append('cv_file_0', cvFileType0 ? pair0?.cv_file : pair0?.cv_file.path);
                }

                if (pair0?.cv_ref_letter) {
                    formData?.append('cv_ref_letter_0', coverLetterType0 ? pair0?.cv_ref_letter : pair0?.cv_ref_letter.path);
                }

                if (pair0?.video_cv) {
                    formData?.append('video_cv_0', videoCVType0 ? pair0?.video_cv : pair0?.video_cv.path);
                }
            } else {
                formData?.append('cvPair', '');
            }

            if (values?.cv_Pair && values?.cv_Pair[1]) {
                const pair1 = values.cv_Pair[1];
                const cvFileType1 = pair1?.cv_file instanceof File;
                const coverLetterType1 = pair1?.cv_ref_letter instanceof File;
                const videoCVType1 = pair1?.video_cv instanceof File;

                if (pair1?.cv_file) {
                    if (pair1?.isPrimary == true) {
                        formData?.append('primary_cv_file', pair1?.cv_file.path);
                    }
                    formData?.append('cv_file_1', cvFileType1 ? pair1?.cv_file : pair1?.cv_file.path);
                }

                if (pair1?.cv_ref_letter) {
                    formData?.append('cv_ref_letter_1', coverLetterType1 ? pair1?.cv_ref_letter : pair1?.cv_ref_letter.path);
                }

                if (pair1?.video_cv) {
                    formData?.append('video_cv_1', videoCVType1 ? pair1?.video_cv : pair1?.video_cv.path);
                }
            } else {
                formData?.append('cvPair', '');
            }

            if (values?.cv_Pair && values?.cv_Pair[2]) {
                const pair2 = values.cv_Pair[2];
                const cvFileType2 = pair2?.cv_file instanceof File;
                const coverLetterType2 = pair2?.cv_ref_letter instanceof File;
                const videoCVType2 = pair2?.video_cv instanceof File;

                if (pair2?.cv_file) {
                    if (pair2?.isPrimary == true) {
                        formData?.append('primary_cv_file', pair2?.cv_file.path);
                    }
                    formData?.append('cv_file_2', cvFileType2 ? pair2?.cv_file : pair2?.cv_file.path);
                }

                if (pair2?.cv_ref_letter) {
                    formData?.append('cv_ref_letter_2', coverLetterType2 ? pair2?.cv_ref_letter : pair2?.cv_ref_letter.path);
                }

                if (pair2?.video_cv) {
                    formData?.append('video_cv_2', videoCVType2 ? pair2?.video_cv : pair2?.video_cv.path);
                }
            } else {
                formData?.append('cvPair', '');
            }

            if (values?.cv_Pair && values?.cv_Pair[3]) {
                const pair3 = values.cv_Pair[3];
                const cvFileType3 = pair3?.cv_file instanceof File;
                const coverLetterType3 = pair3?.cv_ref_letter instanceof File;
                const videoCVType3 = pair3?.video_cv instanceof File;

                if (pair3?.cv_file) {
                    if (pair3?.isPrimary == true) {
                        formData?.append('primary_cv_file', pair3?.cv_file.path);
                    }
                    formData?.append('cv_file_3', cvFileType3 ? pair3?.cv_file : pair3?.cv_file.path);
                }

                if (pair3?.cv_ref_letter) {
                    formData?.append('cv_ref_letter_3', coverLetterType3 ? pair3?.cv_ref_letter : pair3?.cv_ref_letter.path);
                }

                if (pair3?.video_cv) {
                    formData?.append('video_cv_3', videoCVType3 ? pair3?.video_cv : pair3?.video_cv.path);
                }
            } else {
                formData?.append('cvPair', '');
            }

            if (values?.cv_Pair && values?.cv_Pair[4]) {
                const pair4 = values.cv_Pair[4];
                const cvFileType4 = pair4?.cv_file instanceof File;
                const coverLetterType4 = pair4?.cv_ref_letter instanceof File;
                const videoCVType4 = pair4?.video_cv instanceof File;

                if (pair4?.cv_file) {
                    if (pair4?.isPrimary == true) {
                        formData?.append('primary_cv_file', pair4?.cv_file.path);
                    }
                    formData?.append('cv_file_4', cvFileType4 ? pair4?.cv_file : pair4?.cv_file.path);
                }

                if (pair4?.cv_ref_letter) {
                    formData?.append('cv_ref_letter_4', coverLetterType4 ? pair4?.cv_ref_letter : pair4?.cv_ref_letter.path);
                }

                if (pair4?.video_cv) {
                    formData?.append('video_cv_4', videoCVType4 ? pair4?.video_cv : pair4?.video_cv.path);
                }
            } else {
                formData?.append('cvPair', '');
            }
            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => <b>{err.response.data.error}</b>,
            }).then(async () => { 
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                await setEditMode(!editMode)
                await setFormLoading(false); 
            })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });
        },
    });
    const handlePopupClose = (popupName) => {
        setIsOpenCVSelector(false);
    }

    const handlePopupOpen = (popupName) => {
        setIsOpenCVSelector(true);

    }


    const handleAddCVAndCoverLetter = (values) => {
        // Set all existing items in cv_Pair to have isPrimary as false
        const updatedCVPair = formik.values.cv_Pair.map((item) => ({
            ...item,
            isPrimary: false,
        }));

        // Add the new item with isPrimary as true
        const newCVPair = { id: Date.now(), ...values, isPrimary: true };

        // Update formik's cv_Pair field with the updated array
        formik.setFieldValue('cv_Pair', [...updatedCVPair, newCVPair]);

        // Close the popup
        handlePopupClose('CVAndCoverLetter');
    }
    const handleOptionRemove = (field, id) => {
        const filteredField = formik.values[field].filter((option) => option.id !== id);
        formik.setFieldValue(field, filteredField);
    }

    return (
        <div className={styles.container}>
            {/* Second Column */}
            <div className={styles.secondColumn}>
                {!editMode &&
                    <Close
                        className={styles.mobileIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            formik.setFieldValue('cv_Pair', generateCVPairs(user));
                            setEditMode(!editMode)
                        }}
                    />}
                <div className={styles.title}>
                    {t(`profileSidebar.tabs.resumeCoverLetter`)}
                </div>
                {editMode ?
                    <>
                        <div className={styles.aboutTitle}></div>

                        <>
                            {user.verification_status !== 'approved' ?
                                <>

                                    <div>
                                        {(user.cv_file || user.cv_ref_letter || user.video_cv) && (
                                            <div className={styles.user_profile_buttons}>

                                                {user.cv_file && 
                                                    <FlatButton
                                                        disabled={user.hidden}
                                                        icon={download_icon}
                                                        href={process.env.REACT_APP_UPLOADS_PATH + user.cv_file}
                                                        download
                                                        target="_blank">
                                                        {t('job_seeker.download_cv')}
                                                    </FlatButton>
                                                }

                                                {user.cv_ref_letter &&
                                                    <FlatButton
                                                        disabled={user.hidden}
                                                        icon={download_icon}
                                                        href={process.env.REACT_APP_UPLOADS_PATH + user.cv_ref_letter}
                                                        download
                                                        target="_blank">
                                                        {t('job_seeker.download_reference_letter')}
                                                    </FlatButton>}

                                                {user.video_cv &&
                                                    <FlatButton
                                                        disabled={user.hidden}
                                                        icon={download_icon}
                                                        href={process.env.REACT_APP_UPLOADS_PATH + user.video_cv}
                                                        download
                                                        target="_blank">
                                                        {t('job_seeker.download_video_cv')}
                                                    </FlatButton>}

                                            </div>
                                        )}
                                    </div>
                                    <div>
                                    </div>
                                </>
                                :
                                <>
                                    {formik.values?.cv_Pair?.length > 0 &&
                                        <div style={{}}>
                                            <CVPairList

                                                formik={formik}
                                                cv_Pair={formik.values?.cv_Pair}
                                            />
                                        </div>
                                    }
                                </>
                            }
                        </>
                    </>
                    :
                    <div className={styles.editContainer}>
                        <div className={styles.aboutTitle}></div>
                        {user.verification_status !== 'approved' ?
                            <>

                                <div>
                                    <FormFileField
                                        name="cv_file"
                                        type="file"
                                        label={t('edit_profile.job_seeker.cv_file')}
                                        hasBorder
                                        accept=".pdf,.doc,.docx,.odt"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={user.cv_file && (user.cv_file.includes('-name:') ? user.cv_file.split('-name:').pop() : user.cv_file.split('/').pop())}
                                        value={formik.values.cv_file}
                                        error={formik.touched.cv_file && formik.errors.cv_file}
                                    />
                                </div>
                                <div>
                                    <FormFileField
                                        name="cv_ref_letter"
                                        type="file"
                                        label={t('general.UI.Cover_Letter')}
                                        hasBorder
                                        accept=".pdf,.doc,.docx,.odt"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={user.cv_ref_letter && (user.cv_ref_letter.includes('-name:') ? user.cv_ref_letter.split('-name:').pop() : user.cv_ref_letter.split('/').pop())}
                                        value={formik.values.cv_ref_letter}
                                        error={formik.touched.cv_ref_letter && formik.errors.cv_ref_letter}
                                    />
                                </div>
                                <div>
                                    <FormFileField
                                        name="video_cv"
                                        type="file"
                                        label={t('edit_profile.job_seeker.video_cv')}
                                        hasBorder
                                        accept="video/*"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        placeholder={user.video_cv && (user.video_cv.includes('-name:') ? user.video_cv.split('-name:').pop() : user.video_cv.split('/').pop())}
                                        value={formik.values.video_cv}
                                        error={formik.touched.video_cv && formik.errors.video_cv}
                                    />
                                </div>
                            </>
                            :
                            <>
                                <Subtitle
                                    dark
                                    hasButton
                                    buttonText={t('general.UI.add')}
                                    buttonOnClick={() => handlePopupOpen('CVAndCoverLetter')}
                                >
                                    {t(`profileSidebar.tabs.resumeCoverLetter`)}
                                </Subtitle>
                                {formik.values?.cv_Pair?.length > 0 &&
                                    <div style={{}}>
                                        <CVPairList
                                            formik={formik}
                                            cv_Pair={formik.values?.cv_Pair}
                                            onRemove={handleOptionRemove} />
                                    </div>
                                }

                            </>
                        }
                        <Button onClick={formik.submitForm} className={styles.saveButton} >
                            {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                        </Button>
                    </div>
                }
            </div>
            {isOpenCVSelector &&
                <BasicPopup
                    isOpen={isOpenCVSelector}
                    closePopup={() => handlePopupClose('CVAndCoverLetter')}
                >
                    <CVAndCoverLetter user={user} onSubmit={handleAddCVAndCoverLetter} />
                </BasicPopup>
            }
            <div className={styles.thirdColumn}>
                {!editMode ?
                    <Close
                        className={styles.crossIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => {
                            formik.setFieldValue('cv_Pair', generateCVPairs(user));
                            setEditMode(!editMode)
                        }}
                    /> :

                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(!editMode)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                }
            </div>
        </div >
    );
}

export default EditResumeAndCoverLetter;
